import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

/**
 * Wrap protected routes. Redirects to /login while preserving the intended
 * destination so the user lands back where they tried to go after sign-in.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();

  if (state.status === "loading") {
    return <div style={{ padding: 32 }}>{t("common.loading", "Učitavanje…")}</div>;
  }
  if (state.status === "anonymous") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

/**
 * Route guard by function (sprint 7 §5): exactly one of `permission` (one
 * function), `anyOf` (at least one of them) or `allOf` (every one of them —
 * a screen that draws on several modules, e.g. the part search).
 */
export function RequirePermission({
  permission,
  anyOf,
  allOf,
  children,
}: {
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  children: ReactNode;
}) {
  const { state, hasPermission } = useAuth();
  const location = useLocation();
  if (state.status === "loading") return null;

  const hasRequiredPermission = allOf
    ? allOf.every((p) => hasPermission(p))
    : anyOf
      ? anyOf.some((p) => hasPermission(p))
      : permission
        ? hasPermission(permission)
        : false;

  if (state.status === "anonymous") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (!hasRequiredPermission) {
    // Landed here straight from the login page (the pre-login `from` target):
    // the account that just signed in simply cannot see it — go home quietly.
    const afterLogin = (location.state as { afterLogin?: boolean } | null)?.afterLogin === true;
    return <Navigate to={afterLogin ? "/" : "/forbidden"} replace />;
  }
  return <>{children}</>;
}
